/**
 * CreateProjectPage — admin creates a new project with name, colour, description and initial members.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTMAuth } from '@/contexts/TMAuthContext';
import { createProject, getApprovedTMUsers } from '@/services/tmService';
import type { TMUser } from '@/types/tm.types';
import { PROJECT_COLORS } from '@/types/tm.types';
import { cn } from '@/utils/cn';

export default function CreateProjectPage() {
  const navigate = useNavigate();
  const { tmUser } = useTMAuth();

  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [color,       setColor]       = useState('indigo');
  const [memberUids,  setMemberUids]  = useState<string[]>([]);
  const [members,     setMembers]     = useState<TMUser[]>([]);
  const [submitting,  setSubmitting]  = useState(false);

  useEffect(() => {
    getApprovedTMUsers().then(setMembers);
  }, []);

  const toggleMember = (uid: string) =>
    setMemberUids(prev => prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tmUser || !name.trim()) return;
    setSubmitting(true);
    try {
      const id = await createProject({
        name:        name.trim(),
        description: description.trim() || null,
        color,
        memberUids,
        createdBy:   { uid: tmUser.uid, name: tmUser.name, email: tmUser.email },
      });
      toast.success('Project created!');
      navigate(`/tm/projects/${id}`);
    } catch {
      toast.error('Failed to create project');
    }
    setSubmitting(false);
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1.5';

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">New Project</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Name */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <div>
            <label className={labelCls}>Project name <span className="text-red-500">*</span></label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Sunday School Renovation"
              className={inputCls}
              required
            />
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this project about?"
              className={inputCls}
            />
          </div>
        </div>

        {/* Color */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <label className={labelCls}>Project colour</label>
          <div className="flex gap-3 flex-wrap">
            {PROJECT_COLORS.map(c => (
              <button
                key={c.key}
                type="button"
                onClick={() => setColor(c.key)}
                className={cn(
                  'w-9 h-9 rounded-full transition-all relative',
                  c.bg,
                  color === c.key && 'ring-2 ring-offset-2 ring-gray-400 scale-110',
                )}
              >
                {color === c.key && (
                  <Check className="h-4 w-4 text-white absolute inset-0 m-auto" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Members */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <label className={labelCls}>Add members</label>
          <p className="text-xs text-gray-400 mb-3">Only selected members can view this project's tasks.</p>
          {members.length === 0 ? (
            <p className="text-xs text-gray-400">No approved members yet.</p>
          ) : (
            <div className="space-y-2">
              {members.map(m => {
                const selected = memberUids.includes(m.uid);
                return (
                  <button
                    key={m.uid}
                    type="button"
                    onClick={() => toggleMember(m.uid)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all',
                      selected
                        ? 'bg-indigo-50 border-indigo-200'
                        : 'bg-white border-gray-100 hover:bg-gray-50',
                    )}
                  >
                    <div className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold',
                      selected ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500',
                    )}>
                      {selected
                        ? <Check className="h-4 w-4" />
                        : m.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className={cn('text-sm font-semibold truncate', selected ? 'text-indigo-700' : 'text-gray-800')}>
                        {m.name}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{m.email}</p>
                    </div>
                    <span className={cn(
                      'ml-auto shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full capitalize',
                      m.role === 'admin' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500',
                    )}>
                      {m.role}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="w-full py-3.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm active:scale-95 transition-all disabled:opacity-60"
        >
          {submitting ? 'Creating…' : `Create Project${memberUids.length > 0 ? ` · ${memberUids.length} member${memberUids.length !== 1 ? 's' : ''}` : ''}`}
        </button>
      </form>
    </div>
  );
}
